import Modal from "@/components/common/Modal";
import { useAppDispatch } from "@/hooks";
import { setIsApiLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { COUNTRIES } from "@/utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type DictationCourse = {
  id: string;
  courseName?: string;
  name?: string;
  hasCategories?: boolean;
  type?: string;
};

type DictationCategory = {
  id: string;
  name: string;
  courseId: string;
  chapters?: string[];
};

type DictationLesson = {
  id: string;
  name: string;
  courseId: string;
  categoryId?: string;
  chapter?: string;
};

type Sentence = {
  id: string;
  lessonId: string;
  en: string;
  ja: string;
  audio?: string;
  country?: string;
  gender?: string;
  type?: string;
  mp3Url?: string;
};

const sentenceSchema = yup.object().shape({
  id: yup.string().required("センテンスIDが必須です。"),
  lessonId: yup.string().required("レッスンIDが必須です。"),
  en: yup.string().required("英語文が必須です。"),
  ja: yup.string().required("日本語文が必須です。"),
  audio: yup.string(),
  country: yup.string(),
  gender: yup.string(),
  type: yup.string(),
});

const SentenceManagement = () => {
  const dispatch = useAppDispatch();
  
  const sentencesRef = useRef<Sentence[]>([]);
  const [activeSentence, setActiveSentence] = useState<Sentence | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
  const [dataTableKey, setDataTableKey] = useState(0); // Key to force DataTable re-render
  const dataTableRef = useRef<any>(null);
  const ajaxHandlerRef = useRef<((data: any, callback: any) => Promise<void>) | null>(null);

  // Filter states
  const [courses, setCourses] = useState<DictationCourse[]>([]);
  const [categories, setCategories] = useState<DictationCategory[]>([]);
  const [lessons, setLessons] = useState<DictationLesson[]>([]);
  
  const [filterCourse, setFilterCourse] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterChapter, setFilterChapter] = useState("");
  const [filterLesson, setFilterLesson] = useState("");
  
  const [filteredCategories, setFilteredCategories] = useState<DictationCategory[]>([]);
  const [filteredChapters, setFilteredChapters] = useState<string[]>([]);
  const [filteredLessons, setFilteredLessons] = useState<DictationLesson[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(sentenceSchema),
  });

  // Server-side data fetching function for DataTable
  const fetchSentencesForTable = useCallback(async (
    page: number,
    pageSize: number,
    search: string,
    draw: number,
    orderBy?: string,
    orderDir?: string
  ): Promise<{ data: Sentence[]; recordsTotal: number; recordsFiltered: number; draw: number }> => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", pageSize.toString());
      if (search) params.append("search", search);
      
      // Add sorting parameters
      if (orderBy) {
        params.append("orderBy", orderBy);
        params.append("orderDir", orderDir || "asc");
      }
      
      // Add filter parameters
      if (filterLesson) params.append("lessonId", filterLesson);
      else if (filterChapter && filterCategory) {
        params.append("categoryId", filterCategory);
        params.append("chapter", filterChapter);
      } else if (filterCategory) {
        params.append("categoryId", filterCategory);
      } else if (filterCourse) {
        params.append("courseId", filterCourse);
      }
      
      // Don't include MP3 URLs by default for better performance
      params.append("includeMp3", "false");
      
      const response = await API.get(`sentences?${params.toString()}`);
      const { data: responseData } = response.data;
      
      // Handle paginated format
      let sentencesData: Sentence[] = [];
      let totalCount = 0;
      
      if (responseData.data && Array.isArray(responseData.data)) {
        sentencesData = responseData.data;
        totalCount = responseData.pagination?.totalCount || sentencesData.length;
      } else if (Array.isArray(responseData)) {
        // Fallback for old format
        sentencesData = responseData;
        totalCount = sentencesData.length;
      }
      
      // Ensure all sentences have required properties
      const processedSentences = sentencesData.map((sentence: Sentence) => {
        if (!sentence) return null;
        
        return {
          id: sentence.id || "",
          lessonId: sentence.lessonId || "",
          en: sentence.en || "",
          ja: sentence.ja || "",
          audio: sentence.audio || "",
          country: sentence.country || "",
          gender: sentence.gender || "",
          type: sentence.type || "",
          mp3Url: sentence.mp3Url || null,
        };
      }).filter(Boolean) as Sentence[];
      
      return {
        data: processedSentences,
        recordsTotal: totalCount,
        recordsFiltered: totalCount, // API handles filtering, so same as total
        draw: draw,
      };
    } catch (err) {
      console.error(err);
      toast.error("センテンスの取得に失敗しました。");
      return {
        data: [],
        recordsTotal: 0,
        recordsFiltered: 0,
        draw: draw,
      };
    }
  }, [filterCourse, filterCategory, filterChapter, filterLesson]);

  // Create stable ajax handler that doesn't change reference
  useEffect(() => {
    ajaxHandlerRef.current = async (data: any, callback: any) => {
      try {
        const page = Math.floor((data.start || 0) / (data.length || 25)) + 1;
        const pageSize = data.length || 25;
        const search = data.search?.value || "";
        
        // Extract order from DataTable
        // DataTable sends order as array of objects: [{column: 0, dir: 'desc', name: 'id'}]
        // Column mapping: 0=id, 1=lessonId, 2=en, 3=ja
        let orderBy: string = "id"; // Default to id
        let orderDir: "asc" | "desc" = "asc"; // Default to asc
        if (data.order && Array.isArray(data.order) && data.order.length > 0) {
          const firstOrder = data.order[0];
          // Check if it's an object with column and dir properties
          if (firstOrder && typeof firstOrder === "object" && "column" in firstOrder && "dir" in firstOrder) {
            const columnIndex = firstOrder.column;
            const direction = firstOrder.dir;
            const columnMap: { [key: number]: string } = {
              0: "id",
              1: "lessonId",
              2: "en",
              3: "ja",
            };
            orderBy = columnMap[columnIndex] || "id";
            orderDir = (direction === "desc" ? "desc" : "asc") as "asc" | "desc";
            console.log("Extracted order:", { columnIndex, orderBy, orderDir });
          }
        }
        
        const result = await fetchSentencesForTable(
          page,
          pageSize,
          search,
          data.draw || 1,
          orderBy,
          orderDir
        );
        
        // Update sentences ref for event handlers (don't update state to avoid re-render loop)
        sentencesRef.current = result.data;
        
        callback({
          draw: result.draw,
          recordsTotal: result.recordsTotal,
          recordsFiltered: result.recordsFiltered,
          data: result.data,
        });
      } catch (error) {
        console.error("DataTable ajax error:", error);
        callback({
          draw: data.draw || 1,
          recordsTotal: 0,
          recordsFiltered: 0,
          data: [],
        });
      }
    };
  }, [fetchSentencesForTable]);

  const fetchCourses = async () => {
    try {
      const response = await API.get("dictation-courses");
      const { data } = response.data;
      setCourses(data || []);
    } catch (err) {
      console.error(err);
      toast.error("コースの取得に失敗しました。");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await API.get("dictation-categories");
      const { data } = response.data;
      setCategories(data || []);
    } catch (err) {
      console.error(err);
      toast.error("カテゴリの取得に失敗しました。");
    }
  };

  const fetchLessons = async () => {
    try {
      const response = await API.get("dictation-lessons");
      const { data } = response.data;
      setLessons(data || []);
    } catch (err) {
      console.error(err);
      toast.error("レッスンの取得に失敗しました。");
    }
  };

  // Handle audio file selection
  const handleAudioFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        toast.error("音声ファイルを選択してください。");
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("ファイルサイズは10MB以下にしてください。");
        return;
      }
      
      setSelectedAudioFile(file);
    }
  };

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Audio playback function
  const playAudio = useCallback((sentence: Sentence) => {
    if (!sentence.mp3Url) {
      toast.warning("音声ファイルが見つかりません。");
      return;
    }
    
    try {
      const audio = new Audio(sentence.mp3Url);
      audio.volume = 1.0;
      
      audio.play().catch((error) => {
        console.error("MP3 playback failed:", error);
        toast.error("音声の再生に失敗しました。");
      });
      
    } catch (error) {
      console.error("Failed to play audio:", error);
      toast.error("音声の再生に失敗しました。");
    }
  }, []);

  const handleCreateSentence = async (data: any) => {
    // Validate MP3 file is required for creation
    if (!selectedAudioFile) {
      toast.error("音声ファイルの選択は必須です。");
      return;
    }
    
    try {
      dispatch(setIsApiLoading(true));
      setUploadingAudio(true);
      
      // Convert audio file to base64
      const audioBase64 = await convertFileToBase64(selectedAudioFile);
      
      // Clean the data to remove undefined values
      const cleanedData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== undefined && value !== "")
      );
      
      // Add audio base64 data
      cleanedData.audioFile = audioBase64;
      
      await API.post("sentence", cleanedData);
      
      toast.success("センテンスが作成されました。");
      setDataTableKey(prev => prev + 1); // Reload DataTable
      reset();
      setSelectedAudioFile(null);
      document.querySelector<HTMLButtonElement>("#modalSentence .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "センテンスの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
      setUploadingAudio(false);
    }
  };

  const handleUpdateSentence = async (data: any) => {
    if (!activeSentence) return;
    
    try {
      dispatch(setIsApiLoading(true));
      setUploadingAudio(true);
      
      // Remove id from update data since it can't be changed and ensure no undefined values
      const { id, ...updateData } = data;
      
      // Clean the data to remove undefined values
      const cleanedData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );
      
      // Add audio base64 data if file is selected
      if (selectedAudioFile) {
        const audioBase64 = await convertFileToBase64(selectedAudioFile);
        cleanedData.audioFile = audioBase64;
      }
      
      await API.put(`sentence/${activeSentence.id}`, cleanedData);
      
      toast.success("センテンスが更新されました。");
      setDataTableKey(prev => prev + 1); // Reload DataTable
      reset();
      setSelectedAudioFile(null);
      document.querySelector<HTMLButtonElement>("#modalSentence .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "センテンスの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
      setUploadingAudio(false);
    }
  };

  const handleDeleteSentence = useCallback(async (sentence: Sentence) => {
    if (!window.confirm(`「${sentence.id}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`sentence/${sentence.id}`);
      toast.success("センテンスが削除されました。");
      setDataTableKey(prev => prev + 1); // Reload DataTable
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "センテンスの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  }, [dispatch]);

  const openCreateModal = () => {
    setIsEditMode(false);
    setActiveSentence(null);
    setSelectedAudioFile(null);
    reset({
      id: "",
      lessonId: "",
      en: "",
      ja: "",
      audio: "",
      country: "",
      gender: "",
      type: "",
    });
  };

  const openEditModal = useCallback((sentence: Sentence) => {
    setIsEditMode(true);
    setActiveSentence(sentence);
    setSelectedAudioFile(null);
    
    reset({
      id: sentence.id,
      lessonId: sentence.lessonId || "",
      en: sentence.en || "",
      ja: sentence.ja || "",
      audio: sentence.audio || "",
      country: sentence.country || "",
      gender: sentence.gender || "",
      type: sentence.type || "",
    });
  }, [reset]);

  const openDetailModal = useCallback((sentence: Sentence) => {
    setActiveSentence(sentence);
    setShowDetailModal(true);
  }, []);

  useEffect(() => {
    fetchCourses();
    fetchCategories();
    fetchLessons();
    // DataTable will load data automatically via ajax
    console.log("Component mounted, DataTable should load data");
  }, []);

  // Force DataTable reload when filters change (but not on initial mount)
  const prevFiltersRef = useRef<string>("");
  useEffect(() => {
    const currentFilters = `${filterCourse}-${filterCategory}-${filterChapter}-${filterLesson}`;
    const prevFilters = prevFiltersRef.current;
    
    // Only reload if filters actually changed and DataTable is initialized
    if (prevFilters !== "" && currentFilters !== prevFilters && dataTableRef.current) {
      const timer = setTimeout(() => {
        try {
          const table = dataTableRef.current;
          if (table && table.api) {
            table.api().ajax.reload();
          } else if (table && table.ajax && typeof table.ajax.reload === 'function') {
            table.ajax.reload();
          }
        } catch (error) {
          console.error("Error triggering DataTable reload:", error);
        }
      }, 100);
      
      prevFiltersRef.current = currentFilters;
      return () => clearTimeout(timer);
    } else if (prevFilters === "") {
      // First mount - just save current filters
      prevFiltersRef.current = currentFilters;
    }
  }, [filterCourse, filterCategory, filterChapter, filterLesson]);

  // Update filtered data when courses/categories/lessons change
  useEffect(() => {
    if (filterCourse && courses.length > 0) {
      const courseCategories = categories.filter(cat => cat.courseId === filterCourse);
      setFilteredCategories(courseCategories);
      const courseLessons = lessons.filter(lesson => lesson.courseId === filterCourse);
      setFilteredLessons(courseLessons);
    }
  }, [courses, categories, lessons, filterCourse]);

  useEffect(() => {
    if (filterCategory && categories.length > 0) {
      const category = categories.find(cat => cat.id === filterCategory);
      if (category && category.chapters && Array.isArray(category.chapters)) {
        const chapters = category.chapters.filter(chapter => chapter.trim() !== "");
        setFilteredChapters(chapters);
      }
      const categoryLessons = lessons.filter(lesson => lesson.categoryId === filterCategory);
      setFilteredLessons(categoryLessons);
    }
  }, [categories, lessons, filterCategory]);

  useEffect(() => {
    if (filterChapter && filterCategory && lessons.length > 0) {
      const chapterLessons = lessons.filter(lesson => 
        lesson.categoryId === filterCategory && lesson.chapter === filterChapter
      );
      setFilteredLessons(chapterLessons);
    }
  }, [lessons, filterChapter, filterCategory]);


  // Handle filter changes with cascading logic and reload DataTable
  const handleCourseChange = (courseId: string) => {
    setFilterCourse(courseId);
    setFilterCategory("");
    setFilterChapter("");
    setFilterLesson("");
    
    if (courseId) {
      // Filter categories by selected course
      const courseCategories = categories.filter(cat => cat.courseId === courseId);
      setFilteredCategories(courseCategories);
      
      // Filter lessons by selected course
      const courseLessons = lessons.filter(lesson => lesson.courseId === courseId);
      setFilteredLessons(courseLessons);
    } else {
      setFilteredCategories([]);
      setFilteredChapters([]);
      setFilteredLessons([]);
    }
    
    // Reload DataTable with new filter
    setDataTableKey(prev => prev + 1);
  };

  const handleCategoryChange = (categoryId: string) => {
    setFilterCategory(categoryId);
    setFilterChapter("");
    setFilterLesson("");
    
    if (categoryId) {
      // Filter chapters by selected category
      const category = categories.find(cat => cat.id === categoryId);
      if (category && category.chapters && Array.isArray(category.chapters)) {
        const chapters = category.chapters.filter(chapter => chapter.trim() !== "");
        setFilteredChapters(chapters);
      } else {
        setFilteredChapters([]);
      }
      
      // Filter lessons by selected category
      const categoryLessons = lessons.filter(lesson => lesson.categoryId === categoryId);
      setFilteredLessons(categoryLessons);
    } else {
      setFilteredChapters([]);
      // Reset to course lessons if no category selected
      if (filterCourse) {
        const courseLessons = lessons.filter(lesson => lesson.courseId === filterCourse);
        setFilteredLessons(courseLessons);
      }
    }
    
    // Reload DataTable with new filter
    setDataTableKey(prev => prev + 1);
  };

  const handleChapterChange = (chapter: string) => {
    setFilterChapter(chapter);
    setFilterLesson("");
    
    if (chapter && filterCategory) {
      // Filter lessons by selected chapter
      const chapterLessons = lessons.filter(lesson => 
        lesson.categoryId === filterCategory && lesson.chapter === chapter
      );
      setFilteredLessons(chapterLessons);
    } else if (filterCategory) {
      // Reset to category lessons if no chapter selected
      const categoryLessons = lessons.filter(lesson => lesson.categoryId === filterCategory);
      setFilteredLessons(categoryLessons);
    }
    
    // Reload DataTable with new filter
    setDataTableKey(prev => prev + 1);
  };

  const handleLessonChange = (lessonId: string) => {
    setFilterLesson(lessonId);
    // Reload DataTable with new filter
    setDataTableKey(prev => prev + 1);
  };


  // Setup event listeners for table actions
  useEffect(() => {
    const getSentenceByIdLocal = (sentenceId: string): Sentence | null => {
      return sentencesRef.current.find(s => s.id === sentenceId) || null;
    };

    const handleTableClick = async (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.closest(".edit-sentence-btn")) {
        const sentenceId = target.closest(".edit-sentence-btn")?.getAttribute("data-id");
        if (sentenceId) {
          // Fetch sentence details if not in current data
          let sentence = getSentenceByIdLocal(sentenceId);
          if (!sentence) {
            try {
              const response = await API.get(`sentence/${sentenceId}`);
              sentence = response.data.data;
            } catch (err) {
              console.error(err);
              toast.error("センテンスの取得に失敗しました。");
              return;
            }
          }
          if (sentence) {
            openEditModal(sentence);
          }
        }
      }
      
      if (target.closest(".delete-sentence-btn")) {
        const sentenceId = target.closest(".delete-sentence-btn")?.getAttribute("data-id");
        if (sentenceId) {
          // Fetch sentence details if not in current data
          let sentence = getSentenceByIdLocal(sentenceId);
          if (!sentence) {
            try {
              const response = await API.get(`sentence/${sentenceId}`);
              sentence = response.data.data;
            } catch (err) {
              console.error(err);
              toast.error("センテンスの取得に失敗しました。");
              return;
            }
          }
          if (sentence) {
            handleDeleteSentence(sentence);
          }
        }
      }
      
      if (target.closest(".detail-sentence-btn")) {
        const sentenceId = target.closest(".detail-sentence-btn")?.getAttribute("data-id");
        if (sentenceId) {
          // Fetch sentence details if not in current data
          let sentence = getSentenceByIdLocal(sentenceId);
          if (!sentence) {
            try {
              const response = await API.get(`sentence/${sentenceId}`);
              sentence = response.data.data;
            } catch (err) {
              console.error(err);
              toast.error("センテンスの取得に失敗しました。");
              return;
            }
          }
          if (sentence) {
            openDetailModal(sentence);
          }
        }
      }
      
      if (target.closest(".audio-sentence-btn")) {
        const sentenceId = target.closest(".audio-sentence-btn")?.getAttribute("data-id");
        if (sentenceId) {
          // Fetch sentence with MP3 URL if not in current data
          let sentence = getSentenceByIdLocal(sentenceId);
          if (!sentence || !sentence.mp3Url) {
            try {
              const response = await API.get(`sentence/${sentenceId}`);
              sentence = response.data.data;
              // Fetch MP3 URL separately
              const mp3Response = await API.get(`sentences?limit=1&lessonId=${sentence.lessonId}&includeMp3=true&search=${sentenceId}`);
              if (mp3Response.data.data?.data?.[0]?.mp3Url) {
                sentence.mp3Url = mp3Response.data.data.data[0].mp3Url;
              }
            } catch (err) {
              console.error(err);
              toast.error("音声ファイルの取得に失敗しました。");
              return;
            }
          }
          if (sentence) {
            playAudio(sentence);
          }
        }
      }
    };

    document.addEventListener("click", handleTableClick);
    
    return () => {
      document.removeEventListener("click", handleTableClick);
    };
  }, [handleDeleteSentence, openEditModal, openDetailModal, playAudio]);

  return (
    <>
      <Helmet>
        <title>センテンス管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card" style={{ height: 'auto' }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">センテンス管理</h4>
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#modalSentence"
                  onClick={openCreateModal}
                >
                  <i className="fas fa-plus me-2"></i>
                  センテンス追加
                </button>
              </div>
              <div className="card-body">
                {/* Filter Controls */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label>コース</Form.Label>
                          <Form.Select
                            value={filterCourse}
                            onChange={(e) => handleCourseChange(e.target.value)}
                          >
                            <option value="">全てのコース</option>
                            {courses.map((course) => (
                              <option key={course.id} value={course.id}>
                                {course.courseName || course.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                      
                      <div className="col-md-3">
                        <Form.Group>
                          <Form.Label>カテゴリ</Form.Label>
                          <Form.Select
                            value={filterCategory}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            disabled={!filterCourse}
                          >
                            <option value="">全てのカテゴリ</option>
                            {filteredCategories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                      
                      <div className="col-md-2">
                        <Form.Group>
                          <Form.Label>チャプター</Form.Label>
                          <Form.Select
                            value={filterChapter}
                            onChange={(e) => handleChapterChange(e.target.value)}
                            disabled={!filterCategory}
                          >
                            <option value="">全てのチャプター</option>
                            {filteredChapters.map((chapter, index) => (
                              <option key={index} value={chapter}>
                                {chapter}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                      
                      <div className="col-md-2">
                        <Form.Group>
                          <Form.Label>レッスン</Form.Label>
                          <Form.Select
                            value={filterLesson}
                            onChange={(e) => handleLessonChange(e.target.value)}
                            disabled={!filterCourse}
                          >
                            <option value="">全てのレッスン</option>
                            {filteredLessons.map((lesson) => (
                              <option key={lesson.id} value={lesson.id}>
                                {lesson.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                      
                      <div className="col-md-2">
                        <Form.Group>
                          <Form.Label>&nbsp;</Form.Label>
                          <div>
                            <button
                              className="btn btn-outline-secondary btn-sm w-100"
                              onClick={() => {
                                setFilterCourse("");
                                setFilterCategory("");
                                setFilterChapter("");
                                setFilterLesson("");
                                setFilteredCategories([]);
                                setFilteredChapters([]);
                                setFilteredLessons([]);
                                setDataTableKey(prev => prev + 1); // Reload DataTable
                              }}
                            >
                              <i className="fas fa-times me-1"></i>
                              クリア
                            </button>
                          </div>
                        </Form.Group>
                      </div>
                    </div>

                    {/* Sentences Table */}
                    <div className="table-responsive">
                      <DataTable
                        ref={dataTableRef}
                        key={`sentences-table-${dataTableKey}`}
                        data={[]}
                        className="display"
                        options={useMemo(() => ({
                          serverSide: true,
                          processing: true,
                          deferLoading: 0, // Tell DataTable we have 0 records initially, will load via ajax
                          ajax: async (data: any, callback: any) => {
                            // Use ref to get current handler
                            if (ajaxHandlerRef.current) {
                              await ajaxHandlerRef.current(data, callback);
                            }
                          },
                            autoWidth: true,
                            columnDefs: [
                            { targets: "_all", className: "text-start" },
                          ],
                          stripeClasses: [],
                          order: [[0, "asc"]],
                          searching: true,
                          info: true,
                          paging: true,
                          pageLength: 25,
                          lengthMenu: [
                            [10, 25, 50, 100, 500],
                            [10, 25, 50, 100, 500]
                          ],
                          language: {
                            lengthMenu: "レコード数&nbsp;_MENU_",
                            emptyTable: "データがありません。",
                            search: "検索:",
                            processing: "読み込み中...",
                            paginate: {
                              first: "最初",
                              last: "最後",
                              next: "次へ",
                              previous: "前へ",
                            },
                            info: "_START_ - _END_ 件 / 全 _TOTAL_ 件",
                            infoEmpty: "0 件",
                            infoFiltered: "(全 _MAX_ 件からフィルタ)",
                          },
                        }), [])}
                            columns={[
                              {
                                title: "ID",
                                name: "id",
                                data: "id",
                                className: "text-start",
                                orderable: true,
                                width: "100px",
                                render: (data) => data || "-",
                              },
                              {
                                title: "レッスンID",
                                name: "lessonId",
                                data: null,
                                className: "text-start",
                                orderable: true,
                                width: "120px",
                                render: (data, type, row) => {
                                  return row.lessonId || "-";
                                },
                              },
                              {
                                title: "英語",
                                name: "en",
                                data: null,
                                className: "text-start",
                                orderable: true,
                                render: (data, type, row) => {
                                  const text = row.en || "";
                                  return text.length > 50 ? text.substring(0, 50) + "..." : text;
                                },
                              },
                              {
                                title: "日本語",
                                name: "ja",
                                data: null,
                                className: "text-start",
                                orderable: true,
                                render: (data, type, row) => {
                                  const text = row.ja || "";
                                  return text.length > 50 ? text.substring(0, 50) + "..." : text;
                                },
                              },
                              {
                                title: "音声",
                                data: null,
                                className: "text-center",
                                orderable: false,
                                width: "80px",
                                render: (data, type, row) => {
                                  return `
                                    <button class="btn btn-sm btn-outline-success audio-sentence-btn" data-id="${row.id || ''}" title="音声再生">
                                      <i class="fas fa-volume-up"></i>
                                    </button>
                                  `;
                                },
                              },
                              {
                                title: "操作",
                                data: null,
                                orderable: false,
                                className: "text-center",
                                width: "150px",
                                render: (data, type, row) => {
                                  return `
                                    <div class="d-flex justify-content-center gap-1">
                                      <button class="btn btn-sm btn-outline-primary edit-sentence-btn" data-bs-toggle="modal" data-bs-target="#modalSentence" data-id="${row.id || ''}" title="編集">
                                        <i class="fas fa-edit"></i>
                                      </button>
                                      <button class="btn btn-sm btn-outline-danger delete-sentence-btn" data-id="${row.id || ''}" title="削除">
                                        <i class="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  `;
                                },
                              },
                            ]}
                          />
                    </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sentence Modal */}
        <Modal
          id="modalSentence"
          title={isEditMode ? "センテンス編集" : "センテンス作成"}
        >
          <Form onSubmit={handleSubmit(isEditMode ? handleUpdateSentence : handleCreateSentence)}>
            <Form.Group className="mb-3">
              <Form.Label>センテンスID <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="センテンスIDを入力"
                isInvalid={!!errors.id}
                disabled={isEditMode}
                {...register("id")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.id?.message}
              </Form.Control.Feedback>
              {isEditMode && (
                <Form.Text className="text-muted">
                  編集時はIDを変更できません。
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>レッスンID <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="レッスンIDを入力"
                isInvalid={!!errors.lessonId}
                {...register("lessonId")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lessonId?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>英語文 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="英語文を入力"
                isInvalid={!!errors.en}
                {...register("en")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.en?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>日本語文 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="日本語文を入力"
                isInvalid={!!errors.ja}
                {...register("ja")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.ja?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>音声情報</Form.Label>
              <Form.Control
                type="text"
                placeholder="音声情報を入力 (例: Adam - Calm, Smart)"
                {...register("audio")}
              />
              <Form.Text className="text-muted">
                音声の詳細情報を入力してください。
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>国</Form.Label>
                  <Form.Select {...register("country")}>
                    {COUNTRIES.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>性別</Form.Label>
                  <Form.Select {...register("gender")}>
                    <option value="">選択してください</option>
                    <option value="男">男</option>
                    <option value="女">女</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>タイプ</Form.Label>
              <Form.Control
                type="text"
                placeholder="タイプを入力"
                {...register("type")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                音声ファイル (MP3) 
                {!isEditMode && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="file"
                accept="audio/*"
                onChange={handleAudioFileChange}
                required={!isEditMode}
              />
              <Form.Text className="text-muted">
                MP3ファイルをアップロードしてください。最大サイズ: 10MB
                {!isEditMode && " (作成時は必須)"}
              </Form.Text>
              {selectedAudioFile && (
                <div className="mt-2">
                  <small className="text-success">
                    <i className="fas fa-file-audio me-1"></i>
                    選択されたファイル: {selectedAudioFile.name}
                  </small>
                </div>
              )}
              {uploadingAudio && (
                <div className="mt-2">
                  <small className="text-info">
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    アップロード中...
                  </small>
                </div>
              )}
              {!isEditMode && !selectedAudioFile && (
                <div className="mt-2">
                  <small className="text-danger">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    センテンス作成時は音声ファイルが必須です
                  </small>
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={uploadingAudio || (!isEditMode && !selectedAudioFile)}
              >
                {uploadingAudio ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    {isEditMode ? "更新中..." : "作成中..."}
                  </>
                ) : (
                  isEditMode ? "更新" : "作成"
                )}
              </button>
            </div>
          </Form>
        </Modal>

        {/* Detail Modal */}
        {showDetailModal && (
          <Modal
            id="modalDetail"
            title="センテンス詳細"
          >
            {activeSentence && (
              <div className="sentence-detail">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>ID:</strong> {activeSentence.id}
                  </div>
                  <div className="col-md-6">
                    <strong>レッスンID:</strong> {activeSentence.lessonId}
                  </div>
                </div>
                
                <div className="mb-3">
                  <strong>英語文:</strong>
                  <div className="border p-2 mt-1 bg-light">
                    {activeSentence.en}
                  </div>
                </div>
                
                <div className="mb-3">
                  <strong>日本語文:</strong>
                  <div className="border p-2 mt-1 bg-light">
                    {activeSentence.ja}
                  </div>
                </div>
                
                {activeSentence.audio && (
                  <div className="mb-3">
                    <strong>音声情報:</strong>
                    <div className="border p-2 mt-1 bg-light">
                      {activeSentence.audio}
                    </div>
                  </div>
                )}
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>国:</strong> {activeSentence.country || "-"}
                  </div>
                  <div className="col-md-6">
                    <strong>性別:</strong> {activeSentence.gender || "-"}
                  </div>
                </div>
                
                {activeSentence.type && (
                  <div className="mb-3">
                    <strong>タイプ:</strong> {activeSentence.type}
                  </div>
                )}
                
                <div className="d-flex justify-content-between">
                  <button
                    className="btn btn-success"
                    onClick={() => playAudio(activeSentence)}
                  >
                    <i className="fas fa-volume-up me-2"></i>
                    音声再生
                  </button>
                  <button
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                    onClick={() => setShowDetailModal(false)}
                  >
                    閉じる
                  </button>
                </div>
              </div>
            )}
          </Modal>
        )}
      </div>
    </>
  );
};

export default SentenceManagement;